import { isEmpty, omit } from 'lodash'
import { Fragment, memo, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import { Divider } from 'semantic-ui-react'
import styled from 'styled-components'

import Accordion from '@/components/Accordion/Accordion'
import { AppBuilder, TriangleDown } from '@/components/Icons'
import { ImagePreview } from '@/components/ImagePreview'
import { FieldTypes, Variable } from '@/pages/SpaceAppBuilder/utilities/interfaces'
import { convertVariableTypeToFieldType } from '@/pages/SpaceAppBuilder/utilities/variableHelpers'

import messages from './messages'

const StyledTriangleIcon = styled(TriangleDown)`
  width: 10px;
  height: 10px;
  margin: 0 5px;
  transition: transform 0.1s;
  transform: rotate(-90deg);
  fill: #4d4d4d;
`

const StyledButtonRow = styled.button<any>`
  cursor: pointer;
  background: transparent;
  border: 0;
  display: flex;
  margin-left: ${props => (props.isNested ? '20px' : '15px')};
  width: 100%;
`

const StyledVariableLineItem = styled.li`
  list-style-type: none;
  display: flex;
  margin: 8px 0;
  padding: 0;

  &:hover {
    background-color: #f9f9f9;
  }
`

const StyledAccordionTitle = styled.div`
  background-color: transparent;
  display: flex;
  align-items: center;
  width: 100%;
  cursor: pointer;
  font-size: 1rem;
  border: 0;
  outline: 0;
  p {
    font-size: 0.7rem;
    margin: 5px 5px 5px 0;
  }
`

const StyledImg = styled(ImagePreview)`
  height: 24px;
  width: 24px;
  margin-right: 5px;
`

const VariableOverlayContainer = styled.div<any>`
  && {
    position: absolute;
    background-color: white;
    overflow-y: auto;
    max-height: 500px;
    overflow-x: auto;
    margin-top: -15px;
    z-index: 999;
    border: 1px solid #aaaaaa;
    border-radius: 4px;
    padding-left: 5px;
    width: 100%;

    .content {
      background-color: white;
    }
  }
`

const Description = styled.p`
  font-size: 0.7rem;
  margin: 0 5px;
  font-style: italic;
`

const StyledDescription = styled.div`
  margin-left: auto;
  margin-right: 10px;
`

const VariableName = styled.p`
  && {
    font-size: 0.9rem;
    margin: 0;
    text-align: left;
  }
`

const VariablePath = styled.p`
  font-size: 0.7rem;
  margin: 0 5px;
  color: grey;
`

const VariableExample = styled(VariablePath)`
  font-style: italic;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 300px;
`

const TitleStepLabel = styled.p`
  && {
    font-size: 11px;
    font-style: italic;
    margin: 0 5px;
    color: #747474;
  }
`

const StyledLabel = styled.label`
  font-size: 0.9rem;
`

const StyledDivider = styled(Divider)`
  && {
    margin: 5px 0 !important;
    margin-left: 12px !important;
  }
`

const StyledVariableBlock = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  text-align: left;
`

const StyledIcon = styled.div`
  svg {
    height: 20px;
    width: 20px;
    margin-right: 5px;
  }
`

interface VariableOverlayProps {
  onSelectVariable: (variable: Variable) => void
  className?: string
  variables: Array<Variable[]>
  onClose: () => void
  validVariableTypes?: Array<FieldTypes | undefined>
}

const VariableOverlay = ({
  onSelectVariable,
  variables,
  onClose,
  className,
  validVariableTypes
}: VariableOverlayProps) => {
  const intl = useIntl()

  const popOverRef = useRef<any>(null)

  useEffect(() => {
    popOverRef?.current?.focus()
    const handleMousedown = (e: MouseEvent) => {
      if (popOverRef.current && !popOverRef.current.contains(e.target)) {
        onClose()
      }
    }
    const handleKeydown = (e: KeyboardEvent) => {
      e.key === 'Escape' && onClose()
    }
    document.addEventListener('mousedown', handleMousedown)
    document.addEventListener('keydown', handleKeydown)
    return () => {
      document.removeEventListener('keydown', handleKeydown)
      document.removeEventListener('mousedown', handleMousedown)
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [])

  const shouldDisplayNestedVariables = (variable: Variable) => {
    if (validVariableTypes && variable.properties && variable.type === FieldTypes.OBJECT) {
      for (const property of variable.properties) {
        if (
          validVariableTypes?.includes(convertVariableTypeToFieldType(validVariableTypes, property.type)) ||
          (property.type === FieldTypes.OBJECT && shouldDisplayNestedVariables(property))
        ) {
          return true
        }
      }
      return false
    }
    return true
  }

  const variableNotValidTypeForField = (type: string) => {
    return validVariableTypes && !validVariableTypes?.includes(convertVariableTypeToFieldType(validVariableTypes, type))
  }

  const displayVariables = (variable: Variable, indentation: number, isSingleRootOutput: boolean = false) => {
    indentation = indentation + 5
    if (variable.properties && shouldDisplayNestedVariables(variable)) {
      return (
        <Fragment key={variable.name}>
          <div style={{ marginLeft: `${indentation}px` }}>
            <Accordion
              title={
                <StyledAccordionTitle>
                  <StyledVariableBlock>
                    <VariableName>{variable.displayName}</VariableName>
                    <VariablePath>{variable.name}</VariablePath>
                  </StyledVariableBlock>
                </StyledAccordionTitle>
              }
            >
              {[omit(variable, ['properties'])]
                .concat(variable.properties)
                ?.map((nestedVariable: any) => displayVariables(nestedVariable, indentation))}
            </Accordion>
          </div>
        </Fragment>
      )
    }
    const notValid = variableNotValidTypeForField(variable.type)

    if (notValid) return null
    return (
      <StyledVariableLineItem key={variable.name}>
        <StyledButtonRow
          type={'button'}
          isNested={variable.isNested}
          onClick={(e: any) => {
            e.stopPropagation()
            onSelectVariable(variable)
            onClose()
          }}
        >
          <StyledVariableBlock>
            <div style={{ marginLeft: `${indentation}px` }}>
              <VariableName>{variable.displayName}</VariableName>
              <VariablePath>{variable.name}</VariablePath>
              <VariableExample>{variable.example?.toString()}</VariableExample>
              {isSingleRootOutput &&
                !variable.name.startsWith('$.feature.') &&
                !variable.name.startsWith('$.app.') &&
                !variable.name.startsWith('$.config.') &&
                !variable.name.startsWith('$.trigger.') && (
                  <p className="text-xs font-thin">
                    To access properties for this step's output, you will need to run this step by testing the feature
                  </p>
                )}
            </div>
          </StyledVariableBlock>
          <StyledDescription>
            <Description>{variable.type}</Description>
          </StyledDescription>
        </StyledButtonRow>
      </StyledVariableLineItem>
    )
  }

  const noValidVariables = (variable: Variable): boolean | undefined =>
    variableNotValidTypeForField(variable.type) &&
    (variable.properties && shouldDisplayNestedVariables(variable)
      ? [omit(variable, ['properties'])]
          .concat(variable.properties)
          .every(nestedVariable => noValidVariables(nestedVariable))
      : true)

  return (
    <VariableOverlayContainer
      className={className}
      tabIndex={-1}
      ref={popOverRef}
      role="dialog"
      data-testid="variableOverlay"
    >
      {variables.map((stepVariables: Variable[], index: number) => {
        return (
          <Fragment key={index}>
            {!isEmpty(stepVariables[0]) && (
              <Fragment key={stepVariables[0].name}>
                <Accordion
                  defaultOpen={variables.length === 1}
                  title={
                    <StyledAccordionTitle>
                      {stepVariables[0]?.icon && typeof stepVariables[0].icon === 'string' ? (
                        <StyledImg
                          className="variable-overlay-icon"
                          src={stepVariables[0].icon}
                          // @ts-expect-error
                          fallbackImage={AppBuilder}
                          useLoadingImage={false}
                        />
                      ) : (
                        <StyledIcon> {stepVariables[0].icon} </StyledIcon>
                      )}
                      <TitleStepLabel>{stepVariables[0].stepLabel}</TitleStepLabel>
                      <StyledTriangleIcon />
                      <StyledLabel> {stepVariables[0].stepName}</StyledLabel>
                    </StyledAccordionTitle>
                  }
                >
                  {stepVariables.every(
                    variable => noValidVariables(variable) && !variable.name.startsWith('$.trigger.')
                  ) ? (
                    <StyledVariableLineItem key={'variable.name'}>
                      <StyledButtonRow type={'button'} isNested={true} disabled>
                        <StyledVariableBlock>
                          <div style={{ marginLeft: `5px` }}>
                            <VariablePath>{intl.formatMessage(messages.noAvailableVariables)}</VariablePath>
                            <p className="text-xs font-thin">
                              To access properties for this step's output, you will need to run this step by testing the
                              feature
                            </p>
                          </div>
                        </StyledVariableBlock>
                      </StyledButtonRow>
                    </StyledVariableLineItem>
                  ) : (
                    stepVariables.map((variable: Variable) => displayVariables(variable, 5, stepVariables.length === 1))
                  )}
                </Accordion>
                {index !== variables.length - 1 && <StyledDivider />}
              </Fragment>
            )}
          </Fragment>
        )
      })}
    </VariableOverlayContainer>
  )
}

export default memo(VariableOverlay)
